// WHAT: Type definitions and utilities for health metric normal ranges
// WHY:  Provides standard ranges and calculation functions for body composition metrics
// DOMAIN: Health → Withings
// DEPENDENCIES: None (pure type definitions and utility functions)
// RISKS:
//   - NOTE: Default ranges are based on general population - may not match individual needs
//   - NOTE: Gender-specific adjustments are binary - may not handle all cases
//   - NOTE: BMI calculation uses fixed height (183cm) - should be parameterized
//   - NOTE: Range calculations may not account for age variations

/**
 * WHAT:
 *   Normal range definition for a health metric.
 *
 * WHY:
 *   Provides min/max values and description for metric interpretation.
 */
export interface NormRange {
  /** Description of range */
  description: string;
  /** Maximum value */
  max: number;
  /** Minimum value */
  min: number;
  /** Unit of measurement */
  unit: string;
}

/**
 * WHAT:
 *   Collection of normal ranges for all body composition metrics.
 *
 * WHY:
 *   Provides comprehensive range definitions for health assessment.
 */
export interface NormRanges {
  bmi: NormRange;
  bodyFat: NormRange;
  bodyWater: NormRange;
  boneMass: NormRange;
  fatFreeMassPercent: NormRange;
  fatMass: NormRange;
  muscleMass: NormRange;
  visceralFat: NormRange;
  weight: NormRange;
}

/**
 * WHAT:
 *   Settings for customizing normal ranges.
 *
 * WHY:
 *   Enables personalized range adjustments.
 */
export interface NormRangeSettings {
  /** Age in years */
  age: number;
  /** Custom range overrides */
  customRanges: Partial<NormRanges>;
  /** Gender for gender-specific adjustments */
  gender: "female" | "male";
  /** Height in centimeters */
  height: number;
}

/**
 * WHAT:
 *   Standard normal ranges for healthy values.
 *
 * WHY:
 *   Provides baseline ranges for general population.
 *
 * NOTE: Based on general population averages - may not match individual needs
 */
export const DEFAULT_NORM_RANGES: NormRanges = {
  bmi: {
    description: "18.5-25 (normal weight), 25-30 (overweight), > 30 (obese)",
    max: 25,
    min: 18.5,
    unit: "BMI",
  },
  bodyFat: {
    description: "Men: 10-20%, Women: 18-28%",
    max: 20,
    min: 10,
    unit: "%",
  },
  bodyWater: {
    description: "Men: 60-65%, Women: 55-60%",
    max: 65,
    min: 60,
    unit: "%",
  },
  boneMass: {
    description: "2-4% of body weight",
    max: 4,
    min: 2,
    unit: "%",
  },
  fatFreeMassPercent: {
    description: "Men: 70-90%, Women: 65-85% of body weight",
    max: 90,
    min: 70,
    unit: "%",
  },
  fatMass: {
    description: "Calculated based on weight and body fat",
    max: 50,
    min: 0,
    unit: "kg",
  },
  muscleMass: {
    description: "Men: 40-50%, Women: 30-40% of body weight",
    max: 50,
    min: 40,
    unit: "%",
  },
  visceralFat: {
    description: "1-5 (healthy), 6-10 (elevated), > 10 (high)",
    max: 5,
    min: 1,
    unit: "Index",
  },
  weight: {
    description: "Individual (BMI-based)",
    max: 200,
    min: 0,
    unit: "kg",
  },
};

/**
 * WHAT:
 *   Gets gender-adjusted normal ranges.
 *
 * WHY:
 *   Provides ranges adjusted for gender-specific differences.
 *
 * BEHAVIOR:
 *   - Adjusts body fat, muscle mass, fat mass, body water, and fat-free mass
 *   - Uses standard gender differences
 *
 * NOTE: Gender-specific adjustments are binary - may not handle all cases
 *
 * @param {"female" | "male"} gender - Gender for gender-specific adjustments
 *
 * @returns {NormRanges} Gender-adjusted normal ranges
 *
 */
export const getGenderAdjustedRanges = (gender: "female" | "male"): NormRanges => {
  const ranges = { ...DEFAULT_NORM_RANGES };

  if (gender === "female") {
    ranges.bodyFat = { ...ranges.bodyFat, max: 28, min: 18 };
    ranges.muscleMass = { ...ranges.muscleMass, max: 40, min: 30 };
    ranges.fatMass = { ...ranges.fatMass, max: 30, min: 18 };
    ranges.bodyWater = { ...ranges.bodyWater, max: 60, min: 55 };
    ranges.fatFreeMassPercent = {
      ...ranges.fatFreeMassPercent,
      max: 85,
      min: 65,
    };
  }

  return ranges;
};

/**
 * WHAT:
 *   Calculates weight range based on BMI and height.
 *
 * WHY:
 *   Provides personalized weight range for given height.
 *
 * BEHAVIOR:
 *   - Calculates min/max weight for normal BMI range
 *   - Uses gender-specific BMI limits
 *
 * @param {number} height - Height in centimeters
 *
 * @param {"female" | "male"} gender - Gender for gender-specific BMI limits
 *
 * @returns {NormRange} Weight range based on BMI and height
 *
 */
export const calculateWeightRange = (height: number, gender: "female" | "male"): NormRange => {
  const heightM = height / 100;
  const bmiMin = 18.5; // Same for both genders
  const bmiMax = gender === "male" ? 25 : 24;

  const minWeight = bmiMin * heightM * heightM;
  const maxWeight = bmiMax * heightM * heightM;

  return {
    description: `BMI ${String(bmiMin)}-${String(bmiMax)} for ${String(height)}cm`,
    max: Math.round(maxWeight * 10) / 10,
    min: Math.round(minWeight * 10) / 10,
    unit: "kg",
  };
};

/**
 * WHAT:
 *   Calculates fat mass range based on weight and body fat percentage.
 *
 * WHY:
 *   Provides fat mass range for given weight range.
 *
 * BEHAVIOR:
 *   - Uses custom body fat range if provided, otherwise standard
 *   - Calculates fat mass as percentage of weight
 *
 * @param {NormRange} weightRange - Weight range for calculation
 *
 * @param {"female" | "male"} gender - Gender for gender-specific body fat ranges
 *
 * @param {{min: number, max: number}} [customBodyFatRange] - Optional custom body fat range
 *
 * @param {number} [customBodyFatRange.min] - Minimum body fat percentage
 *
 * @param {number} [customBodyFatRange.max] - Maximum body fat percentage
 *
 * @returns {NormRange} Fat mass range based on weight and body fat percentage
 *
 */
export const calculateFatMassRange = (
  weightRange: NormRange,
  gender: "female" | "male",
  customBodyFatRange?: { min: number; max: number },
): NormRange => {
  // NOTE: Use custom body fat values if available, otherwise standard
  const bodyFatMin = customBodyFatRange?.min ?? (gender === "male" ? 10 : 18); // %
  const bodyFatMax = customBodyFatRange?.max ?? (gender === "male" ? 20 : 28); // %

  // NOTE: Fat mass = Weight × (Body fat % / 100)
  const minFatMass = weightRange.min * (bodyFatMin / 100);
  const maxFatMass = weightRange.max * (bodyFatMax / 100);

  return {
    description: `Based on weight (${String(weightRange.min)}-${String(weightRange.max)}kg) and body fat (${String(bodyFatMin)}-${String(bodyFatMax)}%)`,
    max: Math.round(maxFatMass * 10) / 10,
    min: Math.round(minFatMass * 10) / 10,
    unit: "kg",
  };
};

/**
 * WHAT:
 *   Calculates muscle mass range based on weight and muscle mass percentage.
 *
 * WHY:
 *   Provides muscle mass range for given weight range.
 *
 * BEHAVIOR:
 *   - Uses custom muscle mass range if provided, otherwise standard
 *   - Calculates muscle mass as percentage of weight
 *
 * @param {NormRange} weightRange - Weight range for calculation
 *
 * @param {"female" | "male"} gender - Gender for gender-specific muscle mass ranges
 *
 * @param {{min: number, max: number}} [customMuscleMassRange] - Optional custom muscle mass range
 *
 * @param {number} [customMuscleMassRange.min] - Minimum muscle mass percentage
 *
 * @param {number} [customMuscleMassRange.max] - Maximum muscle mass percentage
 *
 * @returns {NormRange} Muscle mass range based on weight and muscle mass percentage
 *
 */
export const calculateMuscleMassRange = (
  weightRange: NormRange,
  gender: "female" | "male",
  customMuscleMassRange?: { min: number; max: number },
): NormRange => {
  // NOTE: Use custom muscle mass values if available, otherwise standard
  const muscleMassMin = customMuscleMassRange?.min ?? (gender === "male" ? 40 : 30); // %
  const muscleMassMax = customMuscleMassRange?.max ?? (gender === "male" ? 50 : 40); // %

  // NOTE: Muscle mass = Weight × (Muscle mass % / 100)
  const minMuscleMass = weightRange.min * (muscleMassMin / 100);
  const maxMuscleMass = weightRange.max * (muscleMassMax / 100);

  return {
    description: `Based on weight (${String(weightRange.min)}-${String(weightRange.max)}kg) and muscle mass (${String(muscleMassMin)}-${String(muscleMassMax)}%)`,
    max: Math.round(maxMuscleMass * 10) / 10,
    min: Math.round(minMuscleMass * 10) / 10,
    unit: "kg",
  };
};

/**
 * WHAT:
 *   Calculates body water range based on weight and body water percentage.
 *
 * WHY:
 *   Provides body water range for given weight range.
 *
 * BEHAVIOR:
 *   - Uses custom body water range if provided, otherwise standard
 *   - Calculates body water as percentage of weight
 *
 * @param {NormRange} weightRange - Weight range for calculation
 *
 * @param {"female" | "male"} gender - Gender for gender-specific body water ranges
 *
 * @param {{min: number, max: number}} [customBodyWaterRange] - Optional custom body water range
 *
 * @param {number} [customBodyWaterRange.min] - Minimum body water percentage
 *
 * @param {number} [customBodyWaterRange.max] - Maximum body water percentage
 *
 * @returns {NormRange} Body water range based on weight and body water percentage
 *
 */
export const calculateBodyWaterRange = (
  weightRange: NormRange,
  gender: "female" | "male",
  customBodyWaterRange?: { min: number; max: number },
): NormRange => {
  // NOTE: Use custom body water values if available, otherwise standard
  const bodyWaterMin = customBodyWaterRange?.min ?? (gender === "male" ? 60 : 55); // %
  const bodyWaterMax = customBodyWaterRange?.max ?? (gender === "male" ? 65 : 60); // %

  // NOTE: Body water = Weight × (Body water % / 100)
  const minBodyWater = weightRange.min * (bodyWaterMin / 100);
  const maxBodyWater = weightRange.max * (bodyWaterMax / 100);

  return {
    description: `Based on weight (${String(weightRange.min)}-${String(weightRange.max)}kg) and body water (${String(bodyWaterMin)}-${String(bodyWaterMax)}%)`,
    max: Math.round(maxBodyWater * 10) / 10,
    min: Math.round(minBodyWater * 10) / 10,
    unit: "kg",
  };
};

/**
 * WHAT:
 *   Calculates fat-free mass percentage range based on body fat percentage.
 *
 * WHY:
 *   Provides fat-free mass percentage range (inverse of body fat).
 *
 * BEHAVIOR:
 *   - Calculates as inverse of body fat percentage
 *   - Highest body fat = lowest lean mass
 *   - Lowest body fat = highest lean mass
 *
 * @param _weightRange - Weight range for calculation (not used but kept for consistency)
 *
 * @param gender - Gender for gender-specific body fat ranges
 *
 * @param {{min: number, max: number}} [customBodyFatRange] - Optional custom body fat range
 *
 * @param {number} [customBodyFatRange.min] - Minimum body fat percentage
 *
 * @param {number} [customBodyFatRange.max] - Maximum body fat percentage
 *
 * @returns {NormRange} Fat-free mass percentage range (inverse of body fat)
 *
 */
export const calculateFatFreeMassPercentRange = (
  // NOTE: weightRange parameter not used in calculation but kept for API compatibility
  // Reason: Fat-free mass percentage is calculated from body fat percentage, not weight range

  _weightRange: NormRange,
  gender: "female" | "male",
  customBodyFatRange?: { min: number; max: number },
): NormRange => {
  // NOTE: Lean mass % = 100% - Body fat %
  const bodyFatMin = customBodyFatRange?.min ?? (gender === "male" ? 10 : 18);
  const bodyFatMax = customBodyFatRange?.max ?? (gender === "male" ? 20 : 28);

  const fatFreeMassMin = 100 - bodyFatMax; // NOTE: Highest body fat = lowest lean mass
  const fatFreeMassMax = 100 - bodyFatMin; // NOTE: Lowest body fat = highest lean mass

  return {
    description: `Based on body fat (${String(bodyFatMin)}-${String(bodyFatMax)}%), lean mass = 100% - body fat`,
    max: Math.round(fatFreeMassMax * 10) / 10,
    min: Math.round(fatFreeMassMin * 10) / 10,
    unit: "%",
  };
};

/**
 * WHAT:
 *   Calculates BMI range (fixed for 183cm height).
 *
 * WHY:
 *   Provides standard BMI range for normal weight.
 *
 * NOTE: Height is hardcoded to 183cm - should be parameterized
 *
 * @returns {NormRange} BMI range for normal weight
 *
 */
export const calculateBMIRange = (): NormRange => {
  return {
    description: "Normal weight for 183cm: 18.5-25 BMI",
    max: 25,
    min: 18.5,
    unit: "BMI",
  };
};
